import { useEffect, useState } from "react";
import axios from "axios";
import { useDateFilterVersion } from "@/lib/useDateFilterVersion";
import { Shell, Head, TotalRow, useEsc, fmtV, fmtQ } from "./ReportShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StatutoryReportsView({ onClose }) {
  const [data, setData] = useState(null);
  const dfv = useDateFilterVersion();
  useEsc(onClose);
  useEffect(() => {
    axios.get(`${API}/reports/gst-overview`).then((r) => setData(r.data)).catch(() => {});
  }, [dfv]);
  if (!data) return null;
  const Card = ({ title, d, testid }) => (
    <div className="flex-1 border border-slate-300 rounded-sm bg-white/60 p-3" data-testid={testid}>
      <div className="text-xs font-black uppercase tracking-widest text-teal-800 mb-2">{title}</div>
      {[["Taxable Value", d.taxable], ["CGST", d.cgst], ["SGST", d.sgst], ["IGST", d.igst]].map(([k, v]) => (
        <div key={k} className="flex justify-between text-sm py-0.5">
          <span className="text-slate-600">{k}</span>
          <span className="font-semibold text-slate-900">{fmtV(v) || "0.00"}</span>
        </div>
      ))}
      <div className="flex justify-between text-sm py-0.5 border-t border-slate-300 mt-1 pt-1">
        <span className="font-bold">Total Tax ({d.count} bills)</span>
        <span className="font-black">{fmtV(d.tax) || "0.00"}</span>
      </div>
    </div>
  );
  return (
    <Shell title="Statutory Reports — GST Summary" onBack={onClose} testid="statutory-view"
      subtitle={<><span>Auto-computed from sale &amp; purchase bills</span><span>{data.period}</span></>}>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        <div className="flex gap-4">
          <Card title="Outward Supplies (GSTR-1 · Sales)" d={data.outward} testid="gst-outward" />
          <Card title="Inward Supplies (Purchases · ITC)" d={data.inward} testid="gst-inward" />
          <div className="w-[240px] border-2 border-teal-700 rounded-sm p-3 flex flex-col justify-center items-center" style={{ background: "#f1e9d6" }} data-testid="gst-net">
            <div className="text-xs font-black uppercase tracking-widest text-teal-800">Net GST Payable</div>
            <div className={`text-2xl font-black mt-2 ${data.net_payable > 0 ? "text-red-700" : "text-emerald-700"}`}>
              {fmtV(Math.abs(data.net_payable)) || "0.00"}
            </div>
            <div className="text-[10px] text-slate-600 mt-1">{data.net_payable >= 0 ? "Output tax − ITC" : "ITC credit balance"}</div>
          </div>
        </div>
        <div>
          <div className="text-xs font-black uppercase tracking-widest text-teal-800 mb-1">Rate-wise Outward Summary</div>
          <Head cols={[{ t: "GST Rate", w: "w-[130px]" }, { t: "Taxable Value", w: "w-[190px]", right: true },
            { t: "Tax Amount", w: "w-[190px]", right: true }, { t: "" }]} />
          <div data-testid="gst-rate-rows">
            {data.rate_wise.map((r, i) => (
              <div key={i} className="flex items-center text-sm text-slate-900 hover:bg-[#f1e9d6]">
                <div className="w-[130px] px-2 py-0.5 font-semibold">{r.rate}%</div>
                <div className="w-[190px] px-2 py-0.5 text-right">{fmtV(r.taxable)}</div>
                <div className="w-[190px] px-2 py-0.5 text-right">{fmtV(r.tax)}</div>
                <div className="flex-1" />
              </div>
            ))}
            {data.rate_wise.length === 0 && <div className="px-2 py-2 text-xs text-slate-500">No sale bills in this period</div>}
          </div>
        </div>
        <div>
          <div className="text-xs font-black uppercase tracking-widest text-teal-800 mb-1">HSN-wise Outward Summary</div>
          <Head cols={[{ t: "HSN / SAC", w: "w-[130px]" }, { t: "Quantity", w: "w-[140px]", right: true },
            { t: "Taxable Value", w: "w-[190px]", right: true }, { t: "Tax Amount", w: "w-[190px]", right: true }, { t: "" }]} />
          <div data-testid="gst-hsn-rows">
            {data.hsn_wise.map((r, i) => (
              <div key={i} className="flex items-center text-sm text-slate-900 hover:bg-[#f1e9d6]">
                <div className="w-[130px] px-2 py-0.5 font-semibold">{r.hsn}</div>
                <div className="w-[140px] px-2 py-0.5 text-right">{fmtQ(r.qty)}</div>
                <div className="w-[190px] px-2 py-0.5 text-right">{fmtV(r.taxable)}</div>
                <div className="w-[190px] px-2 py-0.5 text-right">{fmtV(r.tax)}</div>
                <div className="flex-1" />
              </div>
            ))}
            {data.hsn_wise.length === 0 && <div className="px-2 py-2 text-xs text-slate-500">No sale bills in this period</div>}
          </div>
        </div>
      </div>
      <TotalRow testid="gst-footer" cells={[{ t: "GSTR-1 / GSTR-3B figures auto-derived from bills — file on GST portal" },
        { t: `Net Payable ${fmtV(data.net_payable) || "0.00"}`, w: "w-[280px]", right: true }]} />
    </Shell>
  );
}
